import React, { useState } from 'react';
import { API_CONSTANTS } from '../../utils/api';
import './PlacementTable.css';

const PlacementTable = ({
  placements,
  loading,
  error,
  filters,
  pagination,
  onFilterChange,
  onPageChange,
  onEdit,
  onDelete,
  onBulkDelete,
  onExport,
  onRefresh,
}) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [localFilters, setLocalFilters] = useState({
    company: filters.company || '',
    status: filters.status || '',
    season: filters.season || '',
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(placements.map(p => p.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleFilterChange = (name, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    
    onFilterChange({ [name]: value });
  };

  const handleBulkDelete = () => {
    if (selectedRows.size > 0) {
      onBulkDelete(Array.from(selectedRows));
      setSelectedRows(new Set());
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'Applied': 'badge-primary',
      'Rejected': 'badge-danger',
      'PPO Rejected': 'badge-warning',
      'Session': 'badge-info',
      'Interview Scheduled': 'badge-warning',
      'Offer Received': 'badge-success',
      'Accepted': 'badge-success',
      'Declined': 'badge-secondary',
    };
    return statusClasses[status] || 'badge-secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const renderPriorityStars = (rating) => {
    if (!rating) return '-';
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading placements...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-container">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle error-icon"></i>
          <div className="error-message">Failed to load placements</div>
          <div className="error-description">{error}</div>
          <button className="btn btn-primary mt-3" onClick={onRefresh}>
            <i className="fas fa-refresh me-2"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      {/* Filters and Actions */}
      <div className="table-header">
        <div className="filters-section">
          <div className="filter-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search companies..."
              value={localFilters.company}
              onChange={(e) => handleFilterChange('company', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <select
              className="form-control"
              value={localFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              {API_CONSTANTS.APPLICATION_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <select
              className="form-control"
              value={localFilters.season}
              onChange={(e) => handleFilterChange('season', e.target.value)}
            >
              <option value="">All Seasons</option>
              {API_CONSTANTS.SEASONS.map(season => (
                <option key={season} value={season}>{season}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="actions-section">
          {selectedRows.size > 0 && (
            <button
              className="btn btn-danger btn-sm me-2"
              onClick={handleBulkDelete}
            >
              <i className="fas fa-trash me-1"></i>
              Delete ({selectedRows.size})
            </button>
          )}
          
          <button
            className="btn btn-outline-primary btn-sm me-2"
            onClick={() => onExport('csv')}
          >
            <i className="fas fa-download me-1"></i>
            Export CSV
          </button>
          
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onRefresh}
          >
            <i className="fas fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedRows.size === placements.length && placements.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Company</th>
              <th>Role</th>
              <th>Status</th>
              <th>Application Date</th>
              <th>Priority</th>
              <th>Season</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {placements.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  <i className="fas fa-inbox fa-2x text-muted mb-2"></i>
                  <div>No placements found</div>
                  <small className="text-muted">Try adjusting your filters or add a new placement</small>
                </td>
              </tr>
            ) : (
              placements.map(placement => (
                <tr key={placement.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(placement.id)}
                      onChange={() => handleSelectRow(placement.id)}
                    />
                  </td>
                  <td>
                    <div className="company-cell">
                      <strong>{placement.company_name}</strong>
                      {placement.salary_package && (
                        <small className="text-muted d-block">{placement.salary_package}</small>
                      )}
                    </div>
                  </td>
                  <td>{placement.position_role}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(placement.application_status)}`}>
                      {placement.application_status}
                    </span>
                  </td>
                  <td>{formatDate(placement.application_date)}</td>
                  <td>
                    <span className="priority-stars">
                      {renderPriorityStars(placement.priority_rating)}
                    </span>
                  </td>
                  <td>
                    <small className="text-muted">{placement.season}</small>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-outline-primary btn-sm me-1"
                        onClick={() => onEdit(placement)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => onDelete(placement.id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
          </div>
          
          <nav>
            <ul className="pagination">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}
              
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default PlacementTable;
